import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsArrowUpRight, BsCart } from "react-icons/bs";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ApiPaths } from "../../api/paths";
import CustomButton from "../../components/custom-button/CustomButton";
import DataTable from "../../components/data-table/DataTable";
import AssetsShieldIcon from "../../components/icons/AssetsShieldIcon";
import SearchIcon from "../../components/icons/SearchIcon";
import Pagination from "../../components/pagination/Pagination";
import useData from "../../hooks/use-data";
import useMutateData from "../../hooks/use-mutate-data";
import usePagination from "../../hooks/use-pagination";
import { PaginatedApiResponseType } from "../../types/common";
import { ActionType, ColumnType } from "../../types/data-table";
import { ProductType } from "../../types/product";
import "./PurchaseTraditionalInsurance.css";
import useDisclosure from "../../hooks/use-disclosure";
import { ConnectKitButton } from "connectkit";
import NautilusAsset from "../purchase-cover/nautilus-asset-page/NautilusAsset";
import { useAppContext } from "../../context/app-context";

interface AddToCartButtonProps {
  product: Object;
}

function AddToCartButton({ product }: AddToCartButtonProps) {
  const { keycloakProfile } = useAppContext();
  const { t } = useTranslation(["purchaseTraditionalInsurance"]);
  const { mutate, loading: addToCartLoading } = useMutateData(ApiPaths.cart, {
    onError: () => {
      toast(t("error.add_to_cart", { ns: "purchaseTraditionalInsurance" }), {
        type: "error",
        toastId: "cart-add",
      });
    },
    onSuccess: () => {
      toast(t("success.add_to_cart", { ns: "purchaseTraditionalInsurance" }), {
        type: "success",
      });
    },
  });
  return (
    <CustomButton
      className="primary-button"
      onClick={() => mutate({ ...product, email: keycloakProfile?.email })}
      loading={addToCartLoading}
      disabled={addToCartLoading}
    >
      Add To Cart
    </CustomButton>
  );
}

export default function PurchaseTraditionalInsurance() {
  const { t } = useTranslation(["purchaseTraditionalInsurance", "common"]);
  const { role } = useAppContext();
  const { onHide, onShow, show } = useDisclosure();
  const [searchInput, setSearchInput] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const { search, product_type } = Object.fromEntries(searchParams);
  const pagination = usePagination();
  const params = useMemo(
    () => ({
      params: {
        page: pagination.page,
        limit: pagination.limit,
        product_status: "ACTIVE",
        search,
        product_type,
      },
    }),
    [pagination.page, pagination.limit, search, product_type]
  );
  const { data, loading } = useData<PaginatedApiResponseType<ProductType[]>>(
    `admin/traditional${ApiPaths.products}`,
    {
      options: params,
      onError: () => {
        toast(t("error.fetch_table", { ns: "purchaseTraditionalInsurance" }), {
          type: "error",
        });
      },
    }
  );

  const handleDownload = (
    base64String: string,
    fileName: string = "file.pdf"
  ) => {
    try {
      // Decode base64 string to raw binary data
      const byteCharacters = atob(base64String.split(",")[1]);
      const byteNumbers: number[] = Array.from({
        length: byteCharacters.length,
      });
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.codePointAt(i) ?? 0;
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      // Create an object URL for the Blob
      const url = URL.createObjectURL(blob);

      // Create a link and trigger the download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.append(link);
      link.click();
      link.remove();

      // Clean up the object URL
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download the file. Please try again.");
      console.error("Download error:", error);
    }
  };

  const handleFilter = (key: string, value: string) => {
    pagination.handlePage(1);
    setSearchParams({ ...Object.fromEntries(searchParams), [key]: value });
  };

  const columns: ColumnType[] = [
    {
      accessor: "product_name",
      header: t("table_head.product", { ns: "purchaseTraditionalInsurance" }),
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "product_owner_name",
      header: t("table_head.product_owner", {
        ns: "purchaseTraditionalInsurance",
      }),
      formatDisplay: (row: ProductType) => {
        return (
          <div className="d-flex align-items-center">
            <AssetsShieldIcon />
            <span className="ms-1">{row?.product_owner_name}</span>
          </div>
        );
      },
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "product_type",
      header: t("table_head.product_type", {
        ns: "purchaseTraditionalInsurance",
      }),
      formatDisplay: (row) => {
        return (
          <div>
            {row?.product_type?.map((item: any) => item?.label).join(", ")}
          </div>
        );
      },
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "cover_wording",
      header: t("table_head.product_details", {
        ns: "purchaseTraditionalInsurance",
      }),
      formatDisplay: (row) => (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleDownload(row?.cover_wording);
          }}
          rel="noreferrer"
        >
          Download <BsArrowUpRight />
        </a>
      ),
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "product_status",
      header: t("table_head.product_status", {
        ns: "purchaseTraditionalInsurance",
      }),
      formatDisplay: (row) => {
        return row.product_status ? (
          <button
            className={`status-button px-4 py-2 ${row?.product_status?.toLowerCase()}-status`}
          >
            {row?.product_status}
          </button>
        ) : (
          "-"
        );
      },
      headerAlignment: "center",
      alignment: "center",
    },
  ];

  const actions: ActionType[] = [
    {
      name: "Add To Cart",
      action: (row) => {
        const rowData = row;
        delete rowData?._id;
        return <AddToCartButton product={rowData} />;
      },
    },
    {
      name: "Purchase",
      action: () => {
        return (
          <CustomButton
            className="primary-button-purchase"
            onClick={() => {
              onShow();
            }}
          >
            <BsCart className="me-1" />
            {t("cart.button.purchase", { ns: "components" })}
          </CustomButton>
        );
      },
    },
  ];

  return (
    <div className="p-3 pb-5 p-md-4">
      <NautilusAsset
        onHide={onHide}
        show={show}
        ClustDoc={true}
        redirectURL="https://app.clustdoc.com/cp/10546639-mdqa8v1FozGHOuCexN3FVpwnv0erVi"
      />
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3  mb-md-4">
        <h2 className="primary-title">
          {t("heading", { ns: "purchaseTraditionalInsurance" })}
        </h2>
        <div className="d-md-none">
          <ConnectKitButton.Custom>
            {(props: any) => (
              <div>
                <button
                  className="d-none d-md-flex primary-button add-more-covers bg-dark"
                  style={{ whiteSpace: "nowrap" }}
                  onClick={props.show}
                >
                  {props?.isConnecting
                    ? "Connecting..."
                    : t("cart.button.wallet", { ns: "components" })}
                </button>
              </div>
            )}
          </ConnectKitButton.Custom>
        </div>
      </div>
      <div className="d-flex align-items-center justify-content-between connect-wallet-cont">
        <div className="d-flex">
          <div className="primary-input-group me-md-4">
            <input
              type="search"
              className="primary-search-input"
              placeholder={
                t("placeholder.search", {
                  ns: "purchaseTraditionalInsurance",
                }) as string
              }
              onChange={(e) => setSearchInput(e.target.value)}
              value={searchInput}
            />
            <button
              className="primary-button search-btn"
              type="button"
              onClick={() => handleFilter("search", searchInput)}
            >
              {t("button.search", { ns: "common" })}
            </button>
            <div className="d-md-none search-icon">
              <SearchIcon />
            </div>
          </div>
        </div>
        <div className="d-none d-md-flex">
          {role === "business-user" ? (
            ""
          ) : (
            <ConnectKitButton.Custom>
              {(props: any) => (
                <div>
                  <button
                    className="d-none d-md-flex primary-button add-more-covers bg-dark"
                    style={{ whiteSpace: "nowrap" }}
                    onClick={props.show}
                  >
                    {props?.isConnecting
                      ? "Connecting..."
                      : t("cart.button.wallet", { ns: "components" })}
                  </button>
                </div>
              )}
            </ConnectKitButton.Custom>
          )}
        </div>
      </div>
      <div className="d-md-flex align-items-center justify-content-end mb-3 mb-md-4">
        <Link
          to="/cart"
          className="primary-button view-cart-btn text-decoration-none ms-auto"
        >
          {t("button.view_cart", { ns: "purchaseTraditionalInsurance" })}
        </Link>
      </div>
      <div>
        <div className="buy-covers-table">
          <DataTable
            columns={columns}
            data={data?.data}
            showSerial={true}
            actions={actions}
            loading={loading}
          />
        </div>
        <Pagination total={data?.count ?? 1} pagination={pagination} />
      </div>
    </div>
  );
}
